import React, {
  Fragment,
  ReactElement,
  ReactNode,
} from 'react';
import noop from 'lodash/noop';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import {
  flexRender,
  Header,
  HeaderGroup,
  SortDirection,
  Table,
} from '@tanstack/react-table';
import clsx from 'clsx';
import SortAscIcon from 'core/icons/sortAsc';
import SortDescIcon from 'core/icons/sortDesc';
import {
  getTableColumnProps,
  TABLE_ROW_COMMON_CLASSES,
} from 'core/components/VirtualTable/helpers';
import ToolTip from 'core/components/Tooltip';
import {
  VirtualTableColumnDef,
} from 'core/components/VirtualTable/types';
import isFunction from 'lodash/isFunction';
import {
  isEmptyString,
} from '@populateui/utils';

export const isWindows = /(win32|win64|windows|wince)/i.test(navigator.userAgent);

type Parts = 'header' | 'outerColumn' | 'column' | 'windowsClassName';

type Props<T> = {
  headerGroups: HeaderGroup<T>[];
  className?: string;
  classes?: Partial<Record<Parts, string | ((...params: any[]) => string)>>;
};

type ColumnProps<T> = Header<T, any> & {
  classes?: Partial<Record<Parts, string | ((...params: any[]) => string)>>;
};

type SortingButtonProps = {
  isSorted: false | SortDirection;
};

function SortingButton({ isSorted }: SortingButtonProps) {
  const isAsc = (isSorted as string) === 'asc';
  return (
    <div className="flex flex-col absolute !h-2 !w-2 text-gray-400 top-0 left-0">
      <SortAscIcon
        className={clsx('!text-3xl absolute', { 'text-green-700': isAsc })}
      />
      <SortDescIcon
        className={clsx('!text-3xl absolute top-[100%]', { 'text-green-700': !isAsc })}
      />
    </div>
  );
}

function TableColumn<T>({
  isPlaceholder,
  column,
  getContext,
  classes,
}: ColumnProps<T>) {
  const isSorted = column.getIsSorted();
  const columnDef = omit(column.columnDef, ['className']) as VirtualTableColumnDef<T>;
  const hasTooltip = !isNil(columnDef.tooltip);
  const Container = hasTooltip ? ToolTip : Fragment;
  return (
    <div
      {...getTableColumnProps(
        columnDef,
        isFunction(classes?.outerColumn) ? classes?.outerColumn(column) : classes?.outerColumn,
      )}
    >
      {isPlaceholder ? null : (
        <Container {...(hasTooltip
          ? { title: columnDef.tooltip }
          : {}
        )}
        >
          <div
            role="button"
            tabIndex={-1}
            className={clsx(
              'relative w-full',
              {
                'cursor-pointer select-none': column.getCanSort(),
                'text-center break-words': columnDef.multiLineHeader,
                'text-left truncate': !columnDef.multiLineHeader,
                'pl-3': isSorted,
              },
              isFunction(classes?.column) ? classes?.column(column) : classes?.column,
            )}
            onClick={column.getToggleSortingHandler()}
            onKeyDown={noop}
          >
            {flexRender(
              typeof columnDef?.columnTitle === 'function'
                ? (columnDef.columnTitle as (table: Table<T>) => ReactNode)(getContext().table)
                : columnDef?.columnTitle ?? columnDef?.header,
              getContext(),
            ) as ReactElement}
            {isSorted && <SortingButton isSorted={isSorted} />}
          </div>
        </Container>
      )}
    </div>
  );
}
export default function TableHeader<T>({
  headerGroups,
  className,
  classes,
}: Props<T>) {
  const computedClassName = clsx(
    'populate-table-row-header border-t border-[#dde2ee]',
    'text-xs text-gray-400 font-semibold py-2',
    TABLE_ROW_COMMON_CLASSES,
    isFunction(classes?.header) ? classes?.header() : classes?.header,
  );
  const windowsClassName = classes?.windowsClassName as string;
  return (
    <div
      className={clsx(
        'populate-table-header flex-[0_1_0%] sticky top-0 bg-white z-[10] border border-transparent',
        className,
        {
          [windowsClassName]: isWindows && !isEmptyString(windowsClassName),
          'w-full': !isWindows,
        },
      )}
    >
      {headerGroups.map((headerGroup) => (
        <div
          key={headerGroup.id}
          className={computedClassName}
        >
          {headerGroup.headers.map((header) => (
            <TableColumn
              key={header.id}
              {...header}
              classes={omit(classes, 'header')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
